import { motion } from "framer-motion";
import { Settings2, BarChart3, Target, Globe2, BookOpen, Sparkles } from "lucide-react";
import GlowEffect from "../common/GlowEffect";
import { useAIAgentData } from "../../hooks/useAIAgentData";

// Icon mapping dictionary to resolve string names to Lucide icon components
const iconMap: Record<string, React.ComponentType<any>> = {
  Settings2,
  BarChart3,
  Target,
  Globe2,
  BookOpen,
  Sparkles,
};

function renderIcon(name: string, size: number, className?: string) {
  const Icon = iconMap[name];
  return Icon ? <Icon size={size} className={className} /> : null;
}

// Shared animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12 },
  }),
} as const;

export default function WhyNustartzSection() {
  const { data } = useAIAgentData();
  const { badgeText, title, description, capabilities } = data.whyNustartzSection;

  return (
    <section className="relative px-5 sm:px-10 md:px-16 sm:py-16 py-8">
      <GlowEffect direction="right" />
      <div className="space-y-16">
        <div className="text-center space-y-4">
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-[#A6A0F6] text-sm font-medium tracking-widest uppercase">
            {badgeText}
          </motion.p>
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="text-4xl md:text-6xl font-bold text-white">
            {title}
          </motion.h2>
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} className="text-2xl text-[#d9d9d9] max-w-3xl mx-auto leading-relaxed">
            {description}
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {capabilities.map((cap, i) => (
            <motion.div
              key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
              className="space-y-4 p-6 rounded-2xl border border-white/10 bg-white/[0.03] hover:border-[#A6A0F6]/40 transition-all duration-300"
            >
              <div className="p-3 rounded-xl bg-[#A6A0F6]/15 text-[#A6A0F6] w-fit">{renderIcon(cap.iconName, 24)}</div>
              <h4 className="text-xl text-white font-semibold">{cap.title}</h4>
              <p className="text-lg text-[#d9d9d9] leading-relaxed">{cap.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
